/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import { makeTree, DEFAULT_SAVED_SEARCH_TREE_SORT } from '../helpers/savedSearches';

/**
 * Compose the canonical placeholder path from a parent path and a name. The
 * server enforces the same shape via the path schema.
 */
function buildPlaceholderPath(parentPath, name) {
  if (parentPath) return `${parentPath}${NEW_PATH_DELIMITER}${name}`;
  return `${NEW_PATH_DELIMITER}${name}`;
}

/**
 * Inner thunk — dispatched with `sortType` on the meta arg (injected by the
 * wrapper below) so reducers can rebuild the tree with the same ordering as
 * the saved-searches panel.
 */
export const createEmptyGroupThunk = createAsyncThunk(
  'savedSearches/createEmptyGroup',
  async (
    { parentPath, name, isShared },
    {
      extra: {
        sdk, Logger, Toast, utils, localization: l10n,
      },
      getState,
    },
  ) => {
    try {
      const { data: placeholder } = await sdk.savedSearches.createEmptyGroup(
        name,
        Boolean(isShared),
        parentPath || null,
      );
      placeholder.nodes = [];

      return { placeholder, userId: getState().user._id };
    } catch (error) {
      Logger.error(new Error('Can not create empty saved-search group'), { error }, [
        'CreateSavedSearchEmptyGroupFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);

      const status = utils.getStatusFromResponceError(error);
      const dupMsg = l10n.SAVEDSEARCHESTREE.textDuplicateGroupInLocation;
      Toast(status === 409 ? dupMsg : 'Error creating group', { type: 'error' });

      throw error;
    }
  },
);

/**
 * Creates an empty group; injects current tree sort type. `__pinTop` on the
 * optimistic + fulfilled placeholder keeps the new group at the top of its
 * sibling list (same idea as collections inline create) until the next full
 * list fetch.
 */
export function createEmptyGroup({ parentPath, name, isShared }) {
  return (dispatch, getState) => {
    const state = getState();
    const sortType = state.user?.searchesSortType || DEFAULT_SAVED_SEARCH_TREE_SORT;
    const teamId = isShared
      ? (state.user?.team?._id
        ?? state.savedSearches?.all?.find((ss) => ss?.teamId)?.teamId)
      : undefined;
    return dispatch(createEmptyGroupThunk({
      parentPath,
      name,
      isShared,
      sortType,
      teamId,
      // `savedSearches.userId` is only set after `getSavedSearches.fulfilled`;
      // pass the live session id so pending/rejected `makeTree` calls still
      // resolve favorites (same source as the thunk's fulfilled payload).
      userId: state.user?._id,
    }));
  };
}

export const reducer = (builder) => {
  builder
    .addCase(createEmptyGroupThunk.pending, (state, action) => {
      const {
        parentPath, name, isShared, teamId: sharedTeamId, userId: argUserId,
      } = action.meta.arg;
      const sortType = action.meta.arg.sortType || state.treeSortType || DEFAULT_SAVED_SEARCH_TREE_SORT;
      state.treeSortType = sortType;
      const userId = argUserId ?? state.userId;

      const tempId = `__pendingGroup_${action.meta.requestId}`;
      const optimistic = {
        _id: tempId,
        name,
        path: buildPlaceholderPath(parentPath, name),
        isGroupPlaceholder: true,
        teamId: isShared ? sharedTeamId : undefined,
        // Personal-scope conflict checks use `creatorId` (see pathConflict.isInSavedSearchScope).
        creatorId: isShared ? undefined : userId,
        isBusy: true,
        __pinTop: true,
        createdAt: new Date().toISOString(),
        nodes: [],
      };
      state.all.push(optimistic);
      state.tree = makeTree(state.all, userId, state.searchQuery, sortType);
    })
    .addCase(createEmptyGroupThunk.fulfilled, (state, { meta, payload: { placeholder, userId } }) => {
      const sortType = meta.arg.sortType || state.treeSortType || DEFAULT_SAVED_SEARCH_TREE_SORT;
      state.treeSortType = sortType;

      const tempId = `__pendingGroup_${meta.requestId}`;
      const idx = state.all.findIndex((s) => s && s._id === tempId);
      const realDoc = { ...placeholder, __pinTop: true };
      if (idx >= 0) state.all.splice(idx, 1, realDoc);
      else state.all.push(realDoc);
      state.tree = makeTree(state.all, userId, state.searchQuery, sortType);
    })
    .addCase(createEmptyGroupThunk.rejected, (state, { meta, error }) => {
      state.error = error;
      const sortType = meta.arg.sortType || state.treeSortType || DEFAULT_SAVED_SEARCH_TREE_SORT;
      const userId = meta.arg.userId ?? state.userId;
      const tempId = `__pendingGroup_${meta.requestId}`;
      state.all = state.all.filter((s) => s && s._id !== tempId);
      state.tree = makeTree(state.all, userId, state.searchQuery, sortType);
    });
};
